import { useForm, FormProvider } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useCreateIncubation } from './api'
import { formSchema } from './schema'
import FormBuilder from '../../../../components/app/formBuilder'
import { Button } from '../../../../components/common'
import TickIcon from '../../../../components/common/icons/TickIcon'
import {
  ContactInformation,
  personalInformation,
  addressInformation,
  aboutStartup,
  incubationNeeds,
} from './data'

export default function IncubationForm() {
  const [currentStep, setCurrentStep] = useState(1)
  const [completedSteps, setCompletedSteps] = useState<number[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  // Key to force-remount the form subtree (clears native file inputs)
  const [formKey, setFormKey] = useState(0)
  const methods = useForm({
    resolver: zodResolver(formSchema),
    mode: 'onChange',
  })

  const { mutate } = useCreateIncubation(onSuccess)

  function onSuccess() {
    methods.reset()
    setIsSubmitting(false)
    setFormKey((k) => k + 1)
  }

  const { handleSubmit, trigger } = methods

  const onSubmit = (data: any) => {
    setIsSubmitting(true)
    const formData = new FormData()

    // Format dates properly
    const formatDate = (date: any) => {
      if (!date) return ''
      if (date instanceof Date) return date.toISOString().split('T')[0]
      return new Date(date).toISOString().split('T')[0]
    }

    // Ensure website has proper protocol
    const formatWebsite = (url: string) => {
      if (!url) return ''
      return url.startsWith('http://') || url.startsWith('https://')
        ? url
        : `https://${url}`
    }

    // Required fields
    formData.append('startup_name', data.startup_name)
    formData.append('legal_entity_type', data.legal_entity_type)
    formData.append('registration_date', formatDate(data.registration_date))
    formData.append('website', formatWebsite(data.website))
    formData.append('contact_name', data.contact_name)
    formData.append('contact_designation', data.contact_designation)
    formData.append('contact_email', data.contact_email)
    formData.append('contact_phone', data.contact_phone)
    formData.append('address_street', data.address_street)
    formData.append('address_city', data.address_city)
    formData.append('address_state', data.address_state)
    formData.append('address_zip', data.address_zip)
    formData.append('address_country', data.address_country)
    // Append signature: FormBuilder stores single file at data.signature
    const sig = data.signature
    if (sig instanceof File) {
      formData.append('signature', sig)
    } else if (Array.isArray(sig)) {
      sig.forEach((file: File) => {
        if (file instanceof File) formData.append('signature', file)
      })
    }
    // Optional fields
    const optionalFields = [
      'industry_sector',
      'startup_stage',
      'business_description',
      'solution',
      'unique_value_proposition',
      'target_market',
      'revenue_model',
      'service_interest',
      'resources_needed',
      'key_milestones',
      'previous_programs',
      'additional_comments',
      // 'signature',
    ]

    optionalFields.forEach((field) => {
      if (data[field]) formData.append(field, data[field])
    })

    // Handle special fields
    formData.append('declaration_agreed', data.declaration_agreed ? '0' : '1')
    if (data.declaration_date) {
      formData.append('declaration_date', formatDate(data.declaration_date))
    }

    mutate(formData, {
      onError: () => setIsSubmitting(false),
    })
  }
  const stepFields: Record<number, string[]> = {
    1: [
      'startup_name',
      'legal_entity_type',
      'registration_date',
      'website',
      'contact_name',
      'contact_designation',
      'contact_email',
      'contact_phone',
      'address_street',
      'address_city',
      'address_state',
      'address_zip',
      'address_country',
    ],
    2: [
      'industry_sector',
      'startup_stage',
      'business_description',
      'solution',
      'unique_value_proposition',
      'target_market',
      'revenue_model',
    ],
    3: [
      'service_interest',
      'resources_needed',
      'annual_turnover',
      'problem_statement',
      'key_milestones',
      'previous_programs',
      'additional_comments',
      'declaration_agreed',
      'declaration_date',
    ],
  }

  const nextStep = async () => {
    // Trigger validation for only the current step fields
    const valid = await trigger(stepFields[currentStep])
    if (valid) {
      if (!completedSteps.includes(currentStep)) {
        setCompletedSteps([...completedSteps, currentStep])
      }
      setCurrentStep(currentStep + 1)
    }
  }
  const prevStep = () => setCurrentStep((prev) => Math.max(prev - 1, 1))

  const goToStep = (step: number) => {
    setCurrentStep(step)
  }
  console.log(methods.formState.errors)

  return (
    <div className="w-11/12 mx-auto py-10">
      <FormProvider {...methods}>
        <form
          key={formKey}
          onSubmit={handleSubmit(onSubmit)}
          className="space-y-10"
        >
          <div className="bg-white p-8 rounded-lg">
            {/* Step Indicators */}
            <div className="flex justify-center items-center mb-28 mt-5   ">
              {[1, 2, 3].map((step) => {
                const stepTitles: Record<number, string> = {
                  1: 'Contact Info',
                  2: 'About Startup',
                  3: 'Incubation Needs',
                }

                return (
                  <div
                    key={step}
                    className="flex flex-col items-center cursor-pointer w-[200px] relative"
                    onClick={() => goToStep(step)}
                  >
                    <div className="relative mb-2">
                      {/* Outer Circle - Back */}
                      <div
                        className={`absolute border h-24 w-24 rounded-full left-4 
            ${currentStep === step ? 'border-PrimaryOrange border-2' : 'border-gray-200 border-2'} 
            transition-all duration-300 ease-in-out`}
                      ></div>

                      {/* Inner Circle */}
                      <div
                        className={`absolute h-12 w-12 rounded-full p-[2px] top-[22px] left-[31px] 
            bg-primaryBlue
            transition-all duration-300 ease-in-out`}
                      >
                        <span className="flex items-center justify-center h-full font-bold text-white">
                          {completedSteps.includes(step) ? <TickIcon /> : step}
                        </span>
                      </div>

                      {/* Outer Circle - Front */}
                      <div
                        className={`absolute border h-24 w-24 rounded-full 
            ${currentStep === step ? 'border-PrimaryOrange border-2' : 'border-gray-200 border-2'} 
            transition-all duration-300 ease-in-out`}
                      ></div>
                    </div>

                    {/* Step Title Below the Circle */}
                    <p className="absolute  right-[-65px] top-[100px] text-sm text-gray-600 font-medium  w-[150px]">
                      {stepTitles[step]}
                    </p>
                  </div>
                )
              })}
            </div>

            {currentStep === 1 && (
              <>
                <div className="mb-8">
                  <div className="mb-2">
                    {' '}
                    <h2 className="text-lg font-semibold">
                      Contact Information
                    </h2>
                    <h4 className="text-md font-semibold mt-3">
                      Startup Information
                    </h4>
                    <p className="text-LightGray text-xs">
                      Please Fill Out All Required Fields
                    </p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormBuilder data={ContactInformation} edit />
                  </div>
                </div>

                <div className="mb-8">
                  <h3 className="font-semibold text-base ">
                    Personal Information
                  </h3>
                  <p className="text-LightGray text-xs mb-3">
                    Please Fill Out All Required Fields
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormBuilder data={personalInformation} edit />
                  </div>
                </div>

                <div className="mb-6">
                  <h3 className="font-semibold text-base">
                    Registered Address
                  </h3>
                  <p className="text-LightGray text-xs mb-3">
                    Please Fill Out All Required Fields
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormBuilder data={addressInformation} edit />
                  </div>
                </div>
              </>
            )}

            {currentStep === 2 && (
              <>
                <h2 className="text-lg font-semibold mb-6">
                  About Your Startup
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormBuilder data={aboutStartup} edit />
                </div>
              </>
            )}

            {currentStep === 3 && (
              <>
                <h2 className="text-lg font-semibold mb-6">
                  Incubation Needs & Expectations
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormBuilder data={incubationNeeds} edit />
                </div>
              </>
            )}

            <div
              className={`flex mt-8 ${
                currentStep === 1 ? 'justify-end' : 'justify-between'
              }`}
            >
              {currentStep > 1 && (
                <Button
                  type="button"
                  onClick={prevStep}
                  className="border border-primaryBlue text-orange-500 bg-white px-4 py-2 rounded"
                  label="Previous"
                />
              )}

              {currentStep < 3 ? (
                <Button
                  type="button"
                  onClick={nextStep}
                  className="bg-white0 text-white px-4 py-2 rounded"
                  label="Next"
                />
              ) : (
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className={`bg-white0 text-white px-4 py-2 rounded ${
                    isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  label={isSubmitting ? 'Submitting...' : 'Submit Application'}
                />
              )}
            </div>
          </div>
        </form>
      </FormProvider>
    </div>
  )
}
